import { ResponseProblemJsonBody } from '../models/responseProblemJson';
import { RequestError } from './error';
import {
    DefineEndpointFns,
    DefineEndpointGet,
    DefineEndpointPost,
    DefineEndpointUrl,
    ModelParse,
    EndpointMethodGet,
    EndpointMethodPostLike,
    EndpointMethodUrl,
    EndpointRequestOptions,
    EndpointUrlParams,
    DefineEndpointPut,
    DefineEndpointPatch,
    DefineEndpointDelete,
    RequestDefinition,
} from './types';
import { errorToString } from '@whiz-cart/node-shared/errorToString';
import { createFunctionWithMethods } from '@whiz-cart/node-shared/function';
import { MaybePromise } from '@whiz-cart/node-shared/maybePromise';
import qs from 'qs';
import z from 'zod';

type DefineEndpointParams<Url extends string> =
    EndpointUrlParams<Url> extends Record<string, never> ? [] : [params: EndpointUrlParams<Url>];

/**
 * Create a factory for functions to define endpoints
 *
 * @param backendUrls Either a record from backend name to URL, or a function that returns such a record
 * @param fetchAccessToken Function that fetches an access token, called when an endpoint returns 401
 *
 * @returns Function that makes functions to define endpoints
 */
export function makeEndpointFnsFactory<Backends extends Record<string, string>>(
    backendUrls: Backends | (() => Backends | undefined),
    fetchAccessToken: (refresh?: boolean) => Promise<string | null>,
) {
    /**
     * For a given backend, create functions for defining API endpoints
     *
     * @param backend Backend that contains the API endpoints
     *
     * @returns Functions to define endpoints
     */
    return function makeEndpointFns<Backend extends keyof Backends>(backend: Backend): DefineEndpointFns {
        function resolveUrl(path: string, params: Record<string, string | boolean | number> = {}): string {
            const urls = typeof backendUrls === 'function' ? backendUrls() : backendUrls;
            const backendUrl = urls?.[backend] ?? '';
            const urlWithPlaceholders = `${backendUrl}/${path}`;
            return urlWithPlaceholders.replace(/\{([^}]*)\}/g, (_m, p) => (params as any)?.[p] ?? '');
        }

        const URL = (<Url extends string>(url: Url): EndpointMethodUrl<Url> => {
            return ((...params: DefineEndpointParams<Url>) => {
                return {
                    method: 'URL',
                    url: resolveUrl(url, params[0]),
                };
            }) as EndpointMethodUrl<Url>;
        }) satisfies DefineEndpointUrl;

        // "GET" method
        function makeMethodHandlerGet<Url extends string, TResponse, TQuery extends Record<string, unknown>>(
            url: Url,
            handler: (response: Response) => Promise<TResponse>,
            queryModel?: ModelParse<TQuery>,
        ): EndpointMethodGet<Url, TResponse, TQuery> {
            return ((...params: DefineEndpointParams<Url>) => {
                const resolvedUrl = resolveUrl(url, params[0]);
                return {
                    method: 'GET',
                    url: resolvedUrl,
                    fetch: async (options?: EndpointRequestOptions<TQuery>): Promise<TResponse> => {
                        const response = await makeRequest(
                            'GET',
                            resolvedUrl,
                            null,
                            {
                                ...options,
                                accessToken: fetchAccessToken,
                            },
                            { query: queryModel },
                        );
                        return handler(response);
                    },
                    toRequestInit: async (options?: EndpointRequestOptions<TQuery>): Promise<RequestDefinition> =>
                        makeRequestInit(
                            'GET',
                            resolvedUrl,
                            null,
                            {
                                ...options,
                                accessToken: fetchAccessToken,
                            },
                            { query: queryModel },
                        ),
                };
            }) as unknown as EndpointMethodGet<Url, TResponse, TQuery>;
        }

        const GET = createFunctionWithMethods(
            <Url extends string, TResponse, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                url: Url,
                responseModel: ModelParse<TResponse>,
                requestModels?: { query?: ModelParse<TQuery> },
            ) => makeMethodHandlerGet(url, async (response) => parseModel(responseModel, await response.json()), requestModels?.query),
            {
                arrayBuffer: <Url extends string, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                    url: Url,
                    requestModels?: { query?: ModelParse<TQuery> },
                ) => makeMethodHandlerGet(url, (response) => response.arrayBuffer(), requestModels?.query),
                blob: <Url extends string, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                    url: Url,
                    requestModels?: { query?: ModelParse<TQuery> },
                ) => makeMethodHandlerGet(url, (response) => response.blob(), requestModels?.query),
                formData: <Url extends string, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                    url: Url,
                    requestModels?: { query?: ModelParse<TQuery> },
                ) => makeMethodHandlerGet(url, (response) => response.formData(), requestModels?.query),
                text: <Url extends string, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                    url: Url,
                    requestModels?: { query?: ModelParse<TQuery> },
                ) => makeMethodHandlerGet(url, (response) => response.text(), requestModels?.query),
                void: <Url extends string, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                    url: Url,
                    requestModels?: { query?: ModelParse<TQuery> },
                ) => makeMethodHandlerGet(url, async () => {}, requestModels?.query),
            },
        ) satisfies DefineEndpointGet;

        // "POST", "PATCH", "PUT", "DELETE" methods
        function makeMethodHandlerPost<
            Method extends 'POST' | 'PUT' | 'PATCH' | 'DELETE',
            Url extends string,
            TResponse,
            TBody,
            TQuery extends Record<string, unknown>,
        >(
            method: Method,
            url: Url,
            handler: (response: Response) => Promise<TResponse>,
            requestModels?: {
                query?: ModelParse<TQuery>;
                body?: ModelParse<TBody>;
            },
        ): EndpointMethodPostLike<Method, Url, TResponse, TBody, TQuery> {
            return ((...params: DefineEndpointParams<Url>) => {
                const resolvedUrl = resolveUrl(url, params[0]);
                return {
                    method,
                    url: resolvedUrl,
                    fetch: async (
                        body?: [TBody] extends [never] ? undefined : TBody,
                        options?: EndpointRequestOptions<TQuery>,
                    ): Promise<TResponse> => {
                        const response = await makeRequest(
                            method,
                            resolvedUrl,
                            body,
                            {
                                ...options,
                                accessToken: fetchAccessToken,
                            },
                            requestModels,
                        );
                        return handler(response);
                    },
                    toRequestInit: (
                        body?: [TBody] extends [never] ? undefined : TBody,
                        options?: EndpointRequestOptions<TQuery>,
                    ): Promise<RequestDefinition> =>
                        makeRequestInit(
                            method,
                            resolvedUrl,
                            body,
                            {
                                ...options,
                                accessToken: fetchAccessToken,
                            },
                            requestModels,
                        ),
                };
            }) as unknown as EndpointMethodPostLike<Method, Url, TResponse, TBody, TQuery>;
        }

        function makeEndpointFunctionPost<Method extends 'POST' | 'PUT' | 'PATCH' | 'DELETE'>(
            method: Method,
        ): {
            POST: DefineEndpointPost;
            PUT: DefineEndpointPut;
            PATCH: DefineEndpointPatch;
            DELETE: DefineEndpointDelete;
        }[Method] {
            return createFunctionWithMethods(
                <Url extends string, TResponse, TBody = never, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                    url: Url,
                    responseModel: ModelParse<TResponse>,
                    requestModels?: { body?: ModelParse<TBody>; query?: ModelParse<TQuery> },
                ) =>
                    makeMethodHandlerPost(method, url, async (response) => parseModel(responseModel, await response.json()), requestModels),
                {
                    arrayBuffer: <Url extends string, TBody = never, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                        url: Url,
                        requestModels?: { body?: ModelParse<TBody>; query?: ModelParse<TQuery> },
                    ) => makeMethodHandlerPost(method, url, (response) => response.arrayBuffer(), requestModels),
                    blob: <Url extends string, TBody = never, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                        url: Url,
                        requestModels?: { body?: ModelParse<TBody>; query?: ModelParse<TQuery> },
                    ) => makeMethodHandlerPost(method, url, (response) => response.blob(), requestModels),
                    formData: <Url extends string, TBody = never, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                        url: Url,
                        requestModels?: { body?: ModelParse<TBody>; query?: ModelParse<TQuery> },
                    ) => makeMethodHandlerPost(method, url, (response) => response.formData(), requestModels),
                    text: <Url extends string, TBody = never, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                        url: Url,
                        requestModels?: { body?: ModelParse<TBody>; query?: ModelParse<TQuery> },
                    ) => makeMethodHandlerPost(method, url, (response) => response.text(), requestModels),
                    void: <Url extends string, TBody = never, TQuery extends Record<string, unknown> = Record<string, unknown>>(
                        url: Url,
                        requestModels?: { body?: ModelParse<TBody>; query?: ModelParse<TQuery> },
                    ) => makeMethodHandlerPost(method, url, async () => {}, requestModels),
                },
            ) as {
                POST: DefineEndpointPost;
                PUT: DefineEndpointPut;
                PATCH: DefineEndpointPatch;
                DELETE: DefineEndpointDelete;
            }[Method];
        }

        return {
            URL,
            GET,
            POST: makeEndpointFunctionPost('POST'),
            PUT: makeEndpointFunctionPost('PUT'),
            PATCH: makeEndpointFunctionPost('PATCH'),
            DELETE: makeEndpointFunctionPost('DELETE'),
        };
    };
}

function parseModel<T>(model: ModelParse<T>, value: unknown): T {
    if (model instanceof z.ZodType) {
        return model.parse(value);
    }

    return model(value);
}

async function makeRequestInit(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    resolvedUrl: string,
    body: unknown,
    options: EndpointRequestOptions<any> & {
        isRetry?: boolean;
        accessToken?: string | null | false | ((refresh?: boolean) => MaybePromise<string | null | false>);
    },
    requestModels?: {
        query?: ModelParse<any>;
        body?: ModelParse<any>;
    },
): Promise<[url: string, request: RequestInit]> {
    const headers: Record<string, string> = {};
    const requestUrl = addQueryString(resolvedUrl, options, requestModels?.query);

    const currentAccessToken = options.accessToken instanceof Function ? await options.accessToken() : options.accessToken;
    if (currentAccessToken === null) {
        throw Error('Not logged in!');
    }
    if (currentAccessToken) {
        headers.Authorization = `Bearer ${currentAccessToken}`;
    }

    if (options.key) headers['x-functions-key'] = options.key;

    let requestBody: any;
    if (typeof body !== 'undefined') {
        if (body instanceof FormData) {
            requestBody = body;
        } else {
            const parsedBody = requestModels?.body ? parseModel(requestModels.body, body) : body;
            requestBody = JSON.stringify(parsedBody);
            headers['Content-Type'] = 'application/json';
        }
    }

    return [
        requestUrl,
        {
            method,
            headers: {
                ...headers,
                ...options?.headers,
            },
            signal: options?.signal,
            ...(method === 'GET' ? {} : { body: requestBody }),
        },
    ];
}

async function makeRequest(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    resolvedUrl: string,
    body: unknown,
    options: EndpointRequestOptions<any> & {
        isRetry?: boolean;
        accessToken?: string | null | false | ((refresh?: boolean) => MaybePromise<string | null | false>);
    },
    requestModels?: {
        query?: ModelParse<any>;
        body?: ModelParse<any>;
    },
): Promise<Response> {
    const [requestUrl, requestInit] = await makeRequestInit(method, resolvedUrl, body, options, requestModels);

    try {
        const response = await fetch(requestUrl, requestInit);

        if (response.ok) {
            return response;
        }

        const errorDescription = await parseErrorResponseBody(response);
        throw new RequestError({ method, url: requestUrl }, response, errorDescription);
    } catch (error: any) {
        if (typeof options.accessToken === 'function' && error.response?.status === 401 && !options.isRetry) {
            return makeRequest(
                method,
                resolvedUrl,
                body,
                { ...options, isRetry: true, accessToken: () => typeof options.accessToken === 'function' && options.accessToken(true) },
                requestModels,
            );
        }

        if (error instanceof RequestError) {
            throw error;
        }

        if (error instanceof Error) {
            throw new Error(`${method} ${requestUrl} failed (${errorToString(error)})`, { cause: error });
        }

        error.message = `${method} ${requestUrl} failed (${errorToString(error)})`;
        throw error;
    }
}

function addQueryString(
    resolvedUrl: string,
    options: Pick<EndpointRequestOptions<any>, 'query' | 'queryStringifyOptions'> = {},
    queryModel?: ModelParse<unknown>,
): string {
    if (queryModel) {
        parseModel(queryModel, options.query);
    }

    if (typeof options.query === 'undefined') {
        return resolvedUrl;
    }

    const queryString = qs.stringify(options.query, options.queryStringifyOptions);
    return `${resolvedUrl}?${queryString}`;
}

async function parseErrorResponseBody(errorResponse: Response): Promise<string | undefined> {
    try {
        const contentType = errorResponse.headers.get('Content-Type');

        if (contentType === null) {
            return errorResponse.text();
        }

        if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
            const json = await errorResponse.json();

            const parsedBody = ResponseProblemJsonBody.safeParse(json);
            if (parsedBody.success) {
                if (parsedBody.data.detail) {
                    return parsedBody.data.detail;
                }

                const errorEntries = Object.entries(parsedBody.data.errors ?? {});
                return [parsedBody.data.title, ...errorEntries.map(([key, errors]) => `- ${key}: ${errors.join('; ')}`)].join('\n');
            }

            return JSON.stringify(json);
        }
    } catch (e) {
        console.warn('An error occurred while parsing body of error response:', e);
    }
}
